import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useModalStore } from "../../lib/store/useModalStore";
import { useNavigate, useParams } from "react-router-dom";
import { useTournamentPayInfo } from "../../@apis/hooks/usePay";
import Button from "../../components/atoms/button";
import { formatDate } from "../../lib/utils/dateUtils";

// 라벨/값 한 줄
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-[8px] items-start">
      <p className="min-w-[80px] shrink-0 text-[#8e8e93]">{label}</p>
      <div className="flex-1 min-w-0 text-black break-keep whitespace-pre-line [overflow-wrap:anywhere]">
        {value}
      </div>
    </div>
  );
}

// 실패 안내 화면 (상세 정보 없이 결과만 알릴 때)
function PaymentFailScreen({
  title,
  description,
  onHome,
}: {
  title: string;
  description: string;
  onHome: () => void;
}) {
  return (
    <div className="flex flex-col min-h-[100dvh]">
      <div className="flex-1 bg-[#f2f2f7] flex flex-col items-center justify-center gap-[12px] p-[20px]">
        <div className="bg-[#ff3b30] size-12 rounded-full flex items-center justify-center">
          <span className="text-white text-3xl font-bold leading-none">!</span>
        </div>
        <div className="flex flex-col items-center gap-[8px] text-center w-full">
          <p className="text-3xl font-medium text-black">{title}</p>
          <p className="text-base font-semibold text-[#8e8e93] break-keep whitespace-pre-line">
            {description}
          </p>
        </div>
      </div>

      <div className="p-[12px] flex gap-[10px] bg-white pb-[calc(12px+var(--safe-area-inset-bottom))]">
        <Button
          className="flex-1 font-bold"
          variant="white"
          label="홈으로"
          onClick={onHome}
        />
      </div>
    </div>
  );
}

// 신청 완료 화면 하단 액션(홈으로/접수내역 보기)
function SuccessBottomActions({
  onHome,
  onHistory,
}: {
  onHome: () => void;
  onHistory: () => void;
}) {
  return (
    <div className="p-[12px] flex gap-[10px] bg-white pb-[calc(12px+var(--safe-area-inset-bottom))]">
      <Button
        className="flex-1 font-normal"
        variant="white"
        label="홈으로"
        onClick={onHome}
      />
      <Button
        className="flex-1 font-normal whitespace-pre-line break-keep"
        variant="black"
        label="접수내역 보기"
        onClick={onHistory}
      />
    </div>
  );
}

export default function TournamentPaymentCompletePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openModal } = useModalStore();

  React.useEffect(() => {
    const original = window.closeDialogIfOpened;
    window.closeDialogIfOpened = () => {
      const modals = useModalStore.getState().modals;
      if (modals.length > 0) {
        useModalStore.getState().closeModal(modals[modals.length - 1].id);
        return true;
      }
      openModal("CONFIRM_DIALOG", {
        title: "홈으로 이동",
        content:
          "결제 후에는 뒤로 가실 수 없습니다.\n홈 화면으로 이동하시겠습니까?",
        onClick: () => navigate("/"),
      });
      return true;
    };
    return () => {
      window.closeDialogIfOpened = original;
    };
  }, []);

  const {
    data: payInfo,
    isLoading,
    isError,
  } = useQuery({
    ...useTournamentPayInfo(id as string),
    enabled: !!id && id !== "null",
  });
  const payData = payInfo?.data;

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-[100dvh] items-center justify-center text-[#8e8e93]">
        결제 정보를 불러오는 중입니다...
      </div>
    );
  }

  // API 에러 또는 데이터 없음(잘못된 결제 ID 등)
  if (isError || !payData) {
    return (
      <PaymentFailScreen
        title="결제 실패"
        description="결제가 완료되지 않았습니다."
        onHome={() => navigate("/")}
      />
    );
  }

  // 결제수단 미선택 상태(카드·가상계좌 모두 제공, "CARD,VBANK")
  const payMethod = payData.payMethod ?? "";
  const isMethodNotSelected =
    payMethod.includes("CARD") && payMethod.includes("VBANK");
  const isVbank = !isMethodNotSelected && payMethod.includes("VBANK");
  // 카드: 결제 성공(isPaymentSucceed) / 가상계좌: 발급 성공(isDepositWait) 기준
  const isSuccess = isMethodNotSelected
    ? false
    : isVbank
      ? payData.isDepositWait
      : payData.isPaymentSucceed;

  if (!isSuccess) {
    // 결제수단 미선택으로 실패한 경우 상세 없이 간단한 실패 화면만 노출한다.
    if (isMethodNotSelected) {
      return (
        <PaymentFailScreen
          title="결제 실패"
          description="결제가 완료되지 않았습니다."
          onHome={() => navigate("/")}
        />
      );
    }

    return (
      <div className="flex flex-col min-h-[100dvh]">
        <div className="flex-1 bg-[#f2f2f7] flex flex-col items-center justify-center gap-[20px] p-[20px]">
          <div className="flex flex-col items-center gap-[12px] w-full">
            <div className="bg-[#ff3b30] size-12 rounded-full flex items-center justify-center">
              <span className="text-white text-3xl font-bold leading-none">
                !
              </span>
            </div>
            <div className="flex flex-col items-center gap-[8px] text-center w-full">
              <p className="text-3xl font-medium text-black">결제 실패</p>
              <p className="text-base font-semibold text-[#8e8e93]">
                결제가 완료되지 않았습니다.
              </p>
            </div>
          </div>

          <div className="w-full bg-white border border-[#e5e5ea] rounded-[10px] px-[16px] py-[20px]">
            <div className="flex flex-col gap-[8px] text-sm font-medium">
              <InfoRow
                label="결제상태"
                value={
                  <span className="inline-flex items-center justify-center rounded-[5px] px-[10px] text-xs font-medium bg-[#ffecec] text-[#ff3b30] py-[2.5px]">
                    {payData.paymentStatusLabel || "결제실패"}
                  </span>
                }
              />
              <InfoRow
                label="결제금액"
                value={`${(payData.amt ?? 0).toLocaleString()}원`}
              />
              <InfoRow label="결제수단" value={payData.payMethodLabel || "-"} />
              <InfoRow
                label="결제실패사유"
                value={payData.failReason || payData.message || "-"}
              />
              <InfoRow label="결제실패일시" value={payData.failedAt || "-"} />
            </div>
          </div>
        </div>

        <div className="p-[12px] flex gap-[10px] bg-white pb-[calc(12px+var(--safe-area-inset-bottom))]">
          <Button
            className="flex-1 font-normal"
            variant="white"
            label="홈으로"
            onClick={() => navigate("/")}
          />
        </div>
      </div>
    );
  }

  const isPaid = payData.paymentStatusLabel === "결제완료";
  const badgeClassName = isPaid
    ? "bg-[#f2ffec] text-[#34c759]"
    : "bg-[#fff4d8] text-[#a2845e]";

  const goHome = () => navigate("/");
  // tournamentId 가 있으면 접수내역 상세로, 없으면 목록으로 이동
  const goHistory = () =>
    navigate(
      payData.tournamentId != null
        ? `/setting/tournament-history/${payData.tournamentId}/${payData.applyId}`
        : "/setting/tournament-history",
    );

  // 가상계좌 발급 성공 + 입금대기(결제대기): 입금 계좌 안내 화면
  if (isVbank && !isPaid) {
    const deadline = payData.vBankExpDt
      ? formatDate(payData.vBankExpDt, "YYYY-MM-DD (ddd) HH:mm")
      : null;
    const deadlineMessage = payData.vBankExpDt
      ? formatDate(payData.vBankExpDt, "M월 D일 HH:mm")
      : null;

    const handleCopy = async () => {
      if (!payData.vBankNum) return;
      try {
        await navigator.clipboard?.writeText(payData.vBankNum);
        openModal("ALERT_DIALOG", {
          title: "복사 완료",
          content: "계좌번호가 복사되었습니다.",
        });
      } catch {
        openModal("ALERT_DIALOG", {
          title: "복사 실패",
          content: "계좌번호 복사에 실패했습니다.",
        });
      }
    };

    return (
      <div className="flex flex-col min-h-[100dvh]">
        <div className="flex-1 bg-[#f2f2f7] flex flex-col items-center justify-center gap-[20px] p-[20px]">
          <div className="flex flex-col items-center gap-[12px] w-full">
            <div className="bg-[#ff3b30] size-12 rounded-full flex items-center justify-center">
              <img className="w-5 h-3.5" src="/icons/ic-check.webp" />
            </div>
            <div className="flex flex-col items-center gap-[8px] text-center w-full">
              <p className="text-3xl font-medium  text-black">신청 완료</p>
              <p className="text-base font-semibold text-[#8e8e93] break-keep">
                {deadlineMessage && (
                  <span className="text-[#ff3b30]">{deadlineMessage}</span>
                )}
                까지 아래 계좌로 입금해 주세요.
              </p>
            </div>
          </div>

          <div className="w-full bg-white border border-[#e5e5ea] rounded-[10px] px-[16px] py-[20px] flex flex-col gap-[20px]">
            <div className="flex flex-col gap-[12px]">
              <div className="flex flex-col gap-[8px] text-sm font-medium">
                <InfoRow label="대회명" value={payData.tournamentName || "-"} />
                <InfoRow label="대회날짜" value={payData.calendarDate || "-"} />
                <InfoRow label="대회일정" value={payData.timeType || "-"} />
                <InfoRow
                  label="결제상태"
                  value={
                    <span className="inline-flex items-center justify-center rounded-[5px] px-[10px] text-xs font-medium bg-[#fff4d8] text-[#a2845e] py-[2.5px]">
                      {payData.paymentStatusLabel || "결제대기"}
                    </span>
                  }
                />
                <InfoRow
                  label="결제수단"
                  value={payData.payMethodLabel || "-"}
                />
              </div>

              <div className="flex flex-col gap-[8px]">
                <div className="bg-[#f2f2f7] rounded-[10px] p-[8px] flex flex-col gap-[8px] text-sm font-medium">
                  <div className="flex gap-[8px] items-start">
                    <p className="min-w-[80px] shrink-0 text-[#8e8e93]">
                      입금계좌
                    </p>
                    <p className="flex-1 text-black break-all">
                      {payData.vBankBankName} {payData.vBankNum}
                    </p>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="shrink-0 py-[2px] px-[9px] min-w-[40px] rounded-[5px] border border-[#8e8e93] bg-white text-sm font-medium text-black"
                    >
                      복사
                    </button>
                  </div>
                  <InfoRow
                    label="예금주명"
                    value={payData.vBankAccountHolder || "-"}
                  />
                  {deadline && <InfoRow label="입금기한" value={deadline} />}
                </div>

                <div className="bg-[#f2f2f7] rounded-[10px] p-[12px] flex items-center justify-between">
                  <p className="text-sm font-semibold text-black">입금금액</p>
                  <p className="text-base font-semibold text-[#007aff]">
                    {(payData.amt ?? 0).toLocaleString()}원
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs font-medium text-[#ff3b30]">
              입금기한까지 결제가 완료되지 않으면 접수가 자동으로 취소됩니다.
              접수내역에서 입금 계좌 정보 및 결제 상태를 확인할 수 있습니다.
            </p>
          </div>
        </div>

        <SuccessBottomActions onHome={goHome} onHistory={goHistory} />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[100dvh]">
      <div className="flex-1 bg-[#f2f2f7] flex flex-col items-center justify-center gap-[20px] p-[20px]">
        <div className="flex flex-col items-center gap-[12px] w-full">
          <div className="bg-[#ff3b30] size-12 rounded-full flex items-center justify-center">
            <img className="w-5 h-3.5" src="/icons/ic-check.webp" />
          </div>
          <div className="flex flex-col items-center gap-[8px] text-center w-full">
            <p className="text-3xl font-medium text-black">신청 완료</p>
            <p className="text-base font-semibold text-[#8e8e93]">
              대회 접수가 완료되었습니다.
            </p>
          </div>
        </div>

        <div className="w-full bg-white border border-[#e5e5ea] rounded-[10px] px-[16px] py-[20px]">
          <div className="flex flex-col gap-[8px] text-sm font-medium">
            <InfoRow label="대회명" value={payData.tournamentName || "-"} />
            <InfoRow label="대회날짜" value={payData.calendarDate || "-"} />
            <InfoRow label="대회일정" value={payData.timeType || "-"} />
            <InfoRow
              label="결제상태"
              value={
                <span
                  className={`inline-flex  items-center justify-center rounded-[5px] px-[10px] text-xs font-medium ${badgeClassName} py-[2.5px]`}
                >
                  {payData.paymentStatusLabel || "-"}
                </span>
              }
            />
            <InfoRow label="결제수단" value={payData.payMethodLabel || "-"} />
            <InfoRow
              label="결제금액"
              value={`${(payData.amt ?? 0).toLocaleString()}원`}
            />
            <InfoRow label="결제완료일시" value={payData.paidAt || "-"} />
          </div>
        </div>
      </div>

      <SuccessBottomActions onHome={goHome} onHistory={goHistory} />
    </div>
  );
}
